import * as React from "react"
import { Link } from "react-router"
import { Slot } from "@radix-ui/react-slot"
import { ChevronRight } from "lucide-react"

import { cn } from "@/helpers/classNames"

function Breadcrumb({
  ref,
  ...props
}: { ref?: React.Ref<HTMLElement> } & {
  separator?: React.ReactNode
} & React.ComponentPropsWithoutRef<"nav">) {
  return <nav ref={ref} aria-label="breadcrumb" {...props} />
}

function BreadcrumbList({
  className,
  ref,
  ...props
}: {
  ref?: React.Ref<HTMLOListElement>
} & React.ComponentPropsWithoutRef<"ol">) {
  return (
    <ol
      ref={ref}
      className={cn(
        `
          flex flex-wrap items-center gap-1.5 text-base wrap-break-word
          text-muted-foreground
          sm:gap-2.5
        `,
        className,
      )}
      {...props}
    />
  )
}

function BreadcrumbItem({
  className,
  ref,
  ...props
}: { ref?: React.Ref<HTMLLIElement> } & React.ComponentPropsWithoutRef<"li">) {
  return (
    <li
      ref={ref}
      className={cn("inline-flex items-center gap-1.5", className)}
      {...props}
    />
  )
}

function BreadcrumbLink({
  asChild,
  className,
  ref,
  ...props
}: {
  asChild?: boolean
} & {
  ref?: React.Ref<HTMLAnchorElement>
} & React.ComponentPropsWithoutRef<"a">) {
  const Comp = asChild ? Slot : "a"

  return (
    <Comp
      ref={ref}
      className={cn(
        `
          text-primary-400 transition-colors
          hover:text-primary-300
        `,
        className,
      )}
      {...props}
    />
  )
}

const BreadcrumbSeparator = ({
  children,
  className,
  ...props
}: React.ComponentProps<"li">) => (
  <li
    role="presentation"
    aria-hidden="true"
    className={cn("[&>svg]:size-6", className)}
    {...props}
  >
    {children ?? <ChevronRight />}
  </li>
)
BreadcrumbSeparator.displayName = "BreadcrumbSeparator"

/**
 *
 * Added to shadcn/ui components, as a shortcut to create a breadcrumb for the page
 */
function PageBreadcrumb({
  items,
}: {
  items: { label: React.ReactNode; path?: string }[]
}) {
  return (
    <Breadcrumb>
      <BreadcrumbList>
        {items.map((item, index) => (
          <React.Fragment key={index}>
            <BreadcrumbItem>
              {item.path ? (
                <BreadcrumbLink asChild>
                  <Link to={item.path}>{item.label}</Link>
                </BreadcrumbLink>
              ) : (
                item.label
              )}
            </BreadcrumbItem>
            {index < items.length - 1 && <BreadcrumbSeparator />}
          </React.Fragment>
        ))}
      </BreadcrumbList>
    </Breadcrumb>
  )
}

export { PageBreadcrumb }
